import { useRolodexEdit } from 'features/fde'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { KVAnnotation, KVRooftopAnnotation } from 'types'

import { AnnotationScreenshotControls } from './AnnotationScreenshotControls'

const ScreenshotContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ScreenshotRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`

const ScreenshotPreviewsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1;
`

const ScreenshotPreview = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const PreviewImage = styled.img`
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const PreviewPlaceholder = styled.div`
  width: 120px;
  height: 90px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  text-align: center;
`

const ViewModeLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  min-width: 50px;
`

const RemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  line-height: 1;

  &:hover {
    background: #ef4444;
    border-color: rgba(255, 255, 255, 0.5);
  }
`

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
`

const ExpandedImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 0.5rem;
  cursor: default;
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`

interface AnnotationScreenshotsProps {
  editedAnnotation: KVAnnotation
  setEditedAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
}

export const AnnotationScreenshots: FC<AnnotationScreenshotsProps> = ({
  editedAnnotation,
  setEditedAnnotation,
}) => {
  const [expandedImage, setExpandedImage] = useState<string | null>(null)
  const [hiddenPaths, setHiddenPaths] = useState<Set<string>>(new Set())
  const { rolodexDeleteFile } = useRolodexEdit()
  const { kestrelModel } = useKestrelViewerContext()

  const rooftopAnnotation =
    editedAnnotation.rooftopAnnotation || new KVRooftopAnnotation()

  // Get all screenshot files for this annotation
  const {
    fileRefs: screenshotFiles,
    isLoading: filesLoading,
    forceRefresh: refreshScreenshots,
  } = useRolodexFileLS(
    kestrelModel?.id && editedAnnotation.id
      ? {
          entry_id: kestrelModel.id,
          path: `Screenshots/${editedAnnotation.id}/**`,
        }
      : undefined
  )

  // Get signed URLs for all screenshots
  const { results: screenshotUrls } = useRolodexSignedURLs(
    screenshotFiles.map((file) => ({
      entry_id: kestrelModel?.id || '',
      path: file.path,
    }))
  )

  // Parse and group screenshots by layer prefix
  const groupedScreenshots = useMemo(() => {
    const parsed: Array<{
      layerName: string
      displayName: string
      path: string
      url?: string
      timestamp: number
    }> = []

    screenshotUrls.forEach((result) => {
      if (result.path && result.signed_url) {
        // Skip screenshots that have been hidden (optimistic update)
        if (hiddenPaths.has(result.path)) {
          return
        }

        // Extract layer name from filename
        // Supports: RGB_timestamp.png, DayIR_timestamp.png, Custom_MyLayerName_timestamp.png
        const filename = result.path.split('/').pop()
        if (filename) {
          const match = filename.match(/^(.+)_(\d+)\.(png|jpg)$/)
          if (match) {
            const layerName = match[1]
            const timestamp = parseInt(match[2], 10)

            // Format display name: replace underscores with spaces for custom layers
            const displayName = layerName.replace(/_/g, ' ')

            parsed.push({
              layerName,
              displayName,
              path: result.path,
              url: result.signed_url,
              timestamp,
            })
          }
        }
      }
    })

    // Group by layer name
    const grouped = new Map<string, typeof parsed>()
    parsed.forEach((screenshot) => {
      const existing = grouped.get(screenshot.layerName) || []
      existing.push(screenshot)
      grouped.set(screenshot.layerName, existing)
    })

    // Sort each group by timestamp (newest first) and convert to array
    const result: Array<{
      layerName: string
      displayName: string
      screenshots: typeof parsed
    }> = []

    Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([layerName, screenshots]) => {
        result.push({
          layerName,
          displayName: screenshots[0].displayName,
          screenshots: screenshots.sort((a, b) => b.timestamp - a.timestamp),
        })
      })

    return result
  }, [screenshotUrls, hiddenPaths])

  const removeScreenshot = async (screenshotPath: string) => {
    if (!kestrelModel?.id) return

    // Optimistic update: permanently hide the screenshot
    setHiddenPaths((prev) => new Set(prev).add(screenshotPath))

    try {
      await rolodexDeleteFile(kestrelModel.id, screenshotPath)

      // Also update legacy paths if they match
      const updatedRooftopAnnotation = { ...rooftopAnnotation }
      if (rooftopAnnotation.rgbScreenshotPath === screenshotPath) {
        updatedRooftopAnnotation.rgbScreenshotPath = undefined
      }
      if (rooftopAnnotation.thermalScreenshotPath === screenshotPath) {
        updatedRooftopAnnotation.thermalScreenshotPath = undefined
      }

      setEditedAnnotation((prev) => ({
        ...prev,
        rooftopAnnotation: updatedRooftopAnnotation,
      }))

      // Note: We don't call forceRefreshFiles() to avoid flash.
      // The file is deleted on the server, and hiddenPaths keeps it hidden.
      // The file list will naturally update on next dialog open or page refresh.
    } catch (error) {
      console.error('Screenshot removal error:', error)
      // On error, show it again
      setHiddenPaths((prev) => {
        const next = new Set(prev)
        next.delete(screenshotPath)
        return next
      })
    }
  }

  const viewFullSize = (imageUrl: string) => {
    setExpandedImage(imageUrl)
  }

  const closeFullSize = () => {
    setExpandedImage(null)
  }

  return (
    <>
      <ScreenshotContainer>
        <AnnotationScreenshotControls
          annotationId={editedAnnotation.id}
          kestrelModelId={kestrelModel?.id}
          onSuccess={refreshScreenshots}
        />

        {filesLoading ? (
          <PreviewPlaceholder>Loading screenshots...</PreviewPlaceholder>
        ) : groupedScreenshots.length === 0 ? (
          <PreviewPlaceholder>No screenshots available</PreviewPlaceholder>
        ) : (
          groupedScreenshots.map((group) => (
            <ScreenshotRow key={group.layerName}>
              <ViewModeLabel>{group.displayName}</ViewModeLabel>
              <ScreenshotPreviewsContainer>
                {group.screenshots.map((screenshot) => (
                  <ScreenshotPreview key={screenshot.path}>
                    {screenshot.url ? (
                      <>
                        <PreviewImage
                          src={screenshot.url}
                          alt={`${screenshot.displayName} Screenshot`}
                          onClick={() => viewFullSize(screenshot.url!)}
                        />
                        <RemoveButton
                          onClick={() => removeScreenshot(screenshot.path)}
                          title='Remove screenshot'>
                          ×
                        </RemoveButton>
                      </>
                    ) : (
                      <PreviewPlaceholder>Loading...</PreviewPlaceholder>
                    )}
                  </ScreenshotPreview>
                ))}
              </ScreenshotPreviewsContainer>
            </ScreenshotRow>
          ))
        )}
      </ScreenshotContainer>

      {/* Full Size Image Modal */}
      {expandedImage && (
        <ImageModal onClick={closeFullSize}>
          <ExpandedImage
            src={expandedImage}
            alt='Full Size Screenshot'
            onClick={(e) => e.stopPropagation()}
          />
          <CloseButton onClick={closeFullSize}>×</CloseButton>
        </ImageModal>
      )}
    </>
  )
}
